/**
 * Streaming guide — consuming real-time events from teammate runs.
 *
 * Covers quick patterns, RunStream iteration, event types, context manager,
 * accumulated text, and the non-streaming alternative.
 */

export const streamingContent = `
Runs stream Server-Sent Events (SSE) by default. The SDK wraps these in a \`RunStream\` iterator so you can process events as they arrive.

## Quick Patterns

**Just get the final output:**

\`\`\`python
from m8tes import M8tes

client = M8tes()

with client.runs.create(teammate_id=1, message="Summarize open tickets") as stream:
    for event in stream:
        pass
print(stream.text)
\`\`\`

**Print text as it arrives:**

\`\`\`python
for event in client.runs.create(teammate_id=1, message="Summarize open tickets"):
    if hasattr(event, "delta"):
        print(event.delta, end="", flush=True)
\`\`\`

**Don't need streaming? Use poll:**

\`\`\`python
run = client.runs.create(teammate_id=1, message="Generate report", stream=False)
run = client.runs.poll(run.id)
print(run.output)
\`\`\`

## Context Manager

Use \`with\` to ensure the HTTP connection is closed cleanly:

\`\`\`python
with client.runs.create(teammate_id=1, message="Draft weekly report") as stream:
    for event in stream:
        if hasattr(event, "delta"):
            print(event.delta, end="", flush=True)

# After iteration, get the full accumulated text
print(stream.text)
\`\`\`

\`stream.text\` (or \`stream.output\`) returns all text deltas joined together.

## Event Types

| Event | Description | Key fields |
|-------|-------------|------------|
| \`text-delta\` | Incremental text chunk | \`event.delta\` |
| \`tool-call-start\` | Teammate starts using a tool | \`event.tool_name\` |
| \`tool-call-delta\` | Streaming tool arguments | \`event.delta\` |
| \`tool-result-end\` | Tool finished with result | \`event.result\` |
| \`error\` | Error during execution | \`event.error\` |
| \`done\` | Stream complete | \`event.stop_reason\` (\`end_turn\`, \`max_tokens\`, \`stop_sequence\`) |
| \`run_metrics\` | Execution stats | \`event.execution_time_ms\`, \`event.input_tokens_used\` |

## Filter by Event Type

\`\`\`python
from m8tes import TextDeltaEvent, ToolCallStartEvent

for event in client.runs.create(teammate_id=1, message="Close tickets"):
    if isinstance(event, TextDeltaEvent):
        print(event.delta, end="", flush=True)
    elif isinstance(event, ToolCallStartEvent):
        print(f"\\nUsing tool: {event.tool_name}")
\`\`\`

## Streaming Replies

Follow-up messages also stream by default:

\`\`\`python
run = client.runs.create(teammate_id=1, message="List open tickets", stream=False)
run = client.runs.poll(run.id)

for event in client.runs.reply(run_id=run.id, message="Now close the VIP ones"):
    if hasattr(event, "delta"):
        print(event.delta, end="", flush=True)
\`\`\`

## Non-Streaming Alternative

Pass \`stream=False\` to get a \`Run\` object directly. The run executes in the background — poll for completion:

\`\`\`python
run = client.runs.create(teammate_id=1, message="Generate report", stream=False)
run = client.runs.poll(run.id)
print(run.output)
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"teammate_id": 1, "message": "Generate report", "stream": false}'
\`\`\`

## Best Practices

- Use \`stream=False\` + \`poll()\` for simple scripts and background jobs
- Use streaming for real-time UX (chat interfaces, progress indicators)
- Use the \`with\` context manager to ensure connections are cleaned up
- Access \`stream.text\` after iteration to get the full accumulated output

## SSE Frame Format

Response is a stream of SSE frames:

\`\`\`
data: {"type": "text-delta", "delta": "Here are "}
data: {"type": "text-delta", "delta": "the open tickets..."}
data: {"type": "tool-call-start", "toolName": "gmail_search", "toolCallId": "tc_1"}
data: {"type": "tool-result-end", "toolCallId": "tc_1", "result": "..."}
data: {"type": "done", "stop_reason": "end_turn"}
\`\`\`

## What's Next

- [Runs](/docs/runs) — run creation, follow-ups, and polling
- [Permissions](/docs/permissions) — control tool access with approval modes
`;
